type BadgeVariant = 'neutral' | 'warning' | 'info' | 'success' | 'danger' | 'returned' | 'completed';

/**
 * Real, comprehensive mapping built from every distinct status
 * value actually used across VConnect's modules (Finance, HR,
 * Payroll, Recruitment, Projects) - not a guessed subset. Anything
 * genuinely not in this list falls back to neutral rather than
 * guessing a color that might mean the wrong thing.
 */
const STATUS_VARIANT_MAP: Record<string, BadgeVariant> = {
  // Workflow lifecycle
  draft: 'neutral',
  submitted: 'info',
  pending_approval: 'info',
  under_review: 'warning',
  screening: 'warning',
  returned: 'returned',
  approved: 'success',
  achieved: 'success',
  on_track: 'success',
  active: 'success',
  confirmed: 'success',
  hired: 'success',
  selected: 'success',
  paid: 'success',
  collected: 'success',
  rejected: 'danger',
  off_track: 'danger',
  non_confirmed: 'danger',
  cancelled: 'danger',
  inactive: 'danger',
  suspended: 'danger',
  completed: 'completed',
  closed: 'completed',
  extended: 'warning',
  outstanding: 'warning',
  partially_paid: 'warning',
  partially_collected: 'warning',
  applied: 'info',
  interview: 'info',
  offer: 'info',
  shortlisted: 'info',
  not_started: 'neutral',
  open: 'info',
  on_leave: 'warning',
  probation: 'info',
  separated: 'neutral',
  not_applicable: 'neutral',
  mitigated: 'success',
  clarification_required: 'warning',
  archived: 'neutral',
  in_progress: 'info',
  scheduled: 'info',
  expired: 'neutral',
  hr_review: 'warning',
  signed: 'success',
  finalised: 'completed',
  terminated: 'danger',
};

interface StatusBadgeProps {
  status: string;
  /** Optional - overrides the display text (e.g. a pre-formatted label like "Under Review" instead of the raw "under_review" key). */
  label?: string;
}

/**
 * The single, shared status rendering component for all of
 * VConnect - replaces the plain-text status rendering used
 * throughout the app before this, with the real
 * .status-badge-{variant} classes that already existed but were
 * never actually used anywhere.
 */
export function StatusBadge({ status, label }: StatusBadgeProps) {
  const variant = STATUS_VARIANT_MAP[status] ?? 'neutral';
  const displayText = label ?? status.replace(/_/g, ' ');
  return <span className={`status-badge status-badge-${variant}`}>{displayText}</span>;
}
